import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { BLOGS_PER_PAGE } from '../../Constants';

const BLOG_BASE_URL = 'https://bfhb.gfm.co.uk/blog/wp-json/wp/v2';
const SEARCH_PARAMS =
  '&_embed=wp:featuredmedia&_fields=_links.wp:featuredmedia,_embedded,acf,title,content,excerpt,slug,featured_image,id';

const fetchImage = async (post) => {
  if (post.acf?.mast_image) {
    try {
      const mediaResponse = await fetch(`${BLOG_BASE_URL}/media/${post.acf?.mast_image}`);
      const mediaData = await mediaResponse.json();

      return {
        ...post,
        pageHeaderImage: mediaData.source_url,
      };
    } catch (error) {
      console.error('Error fetching image:', error);
      return {
        ...post,
        pageHeaderImage: null,
      };
    }
  }

  return post;
};

const processPostData = async (post) => {
  const featuredImageUrl = post._embedded?.['wp:featuredmedia']?.[0]?.source_url || null;

  const updatedPost = await fetchImage(post);

  return {
    ...updatedPost,
    cardHeaderUrl: featuredImageUrl,
  };
};

export const fetchBlogPost = createAsyncThunk('blog/fetchBlogPost', async (slug) => {
  const response = await fetch(`${BLOG_BASE_URL}/posts?slug=${slug}${SEARCH_PARAMS}`);
  const data = await response.json();
  const post = data[0];

  if (!post) {
    throw new Error('Post not found');
  }

  return processPostData(post);
});

// Fetch total blog post count (only once)
export const fetchTotalPosts = createAsyncThunk('blog/fetchTotalPosts', async () => {
  const response = await fetch(`${BLOG_BASE_URL}/posts?per_page=1`);
  return Number(response.headers.get('X-WP-Total'));
});

// Async Thunk to Fetch Blog Posts
export const fetchBlogs = createAsyncThunk('blog/fetchBlogs', async ({ page }, { getState }) => {
  const { postsData, totalPosts } = getState().blog;

  // Prevent fetching if we've already fetched all posts
  if (totalPosts && postsData.length >= totalPosts) return []; // Stop fetching if all posts are loaded

  const response = await fetch(`${BLOG_BASE_URL}/posts?per_page=${BLOGS_PER_PAGE}&page=${page}${SEARCH_PARAMS}`);
  const newPosts = await response.json();

  // Process posts to extract featured image and mast_image
  const updatedPosts = await Promise.all(newPosts.map(async (post) => processPostData(post)));

  return updatedPosts;
});

const initialState = {
  postsData: [],
  totalPosts: null,
  page: 1,
  loading: false,
  blogError: null,
  blogPosts: {},
};

const setLoading = (state) => {
  state.loading = true;
  state.blogError = null; // Reset errors when starting a request
};

const setError = (state, action) => {
  state.loading = false;
  state.blogError = action.error?.message || 'An error occurred';
};

const blogSlice = createSlice({
  name: 'blog',
  initialState,
  reducers: {
    setPage: (state, action) => {
      state.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTotalPosts.fulfilled, (state, action) => {
        state.totalPosts = action.payload;
      })
      // Fetch Blogs
      .addCase(fetchBlogs.pending, setLoading)
      .addCase(fetchBlogs.fulfilled, (state, action) => {
        const uniquePosts = action.payload.filter(
          (post) => !state.postsData.some((existingPost) => existingPost.id === post.id),
        );
        state.postsData = [...state.postsData, ...uniquePosts];
        state.loading = false;

        // Store posts in blogPosts state for prefetched data
        uniquePosts.forEach((post) => {
          state.blogPosts[post.slug] = post;
        });
      })
      .addCase(fetchBlogs.rejected, setError)
      // Fetch Single Blog Post
      .addCase(fetchBlogPost.pending, (state) => {
        if (!state.blogError) {
          // Don't reset if an error exists
          state.loading = true;
        }
      })
      .addCase(fetchBlogPost.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.blogPosts[action.payload.slug] = action.payload;
        }
      })
      .addCase(fetchBlogPost.rejected, setError);
  },
});

export const blogReducer = blogSlice.reducer;

export const { setPage } = blogSlice.actions;

export const selectBlogSpecific = (blog) => (state) => state.blog.blogPosts[blog];
export const selectBlogPostsData = (state) => state.blog.postsData;
export const selectBlogLoading = (state) => state.blog.loading;
export const selectBlogError = (state) => state.blog.error;
export const selectBlogPage = (state) => state.blog.page;
export const selectBlogTotalPosts = (state) => state.blog.totalPosts;
